import { useMemo, useState } from "react";
import { StepCreatorSelectProps } from "../CreateBriefTypes.ts";
import { fullFormatNumbers } from "../../../../../helpers/functions/formatNumber.ts";
import {
  RevAvatar,
  RevEmptyState,
  RevInput,
  RevSpinner,
} from "@revmatics/catalyst";
import { faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons";
import { useDebounce } from "../../../../../helpers/hooks/useDebounce.tsx";

export const StepCreatorSelect: React.FC<StepCreatorSelectProps> = ({
  creators,
  selectedCreatorId,
  onSelectCreator,
  isLoading,
  error,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const filteredCreators = useMemo(() => {
    const allCreators = creators;

    if (!debouncedSearchQuery) {
      return allCreators;
    }

    const lowerCaseSearch = debouncedSearchQuery.toLowerCase();
    return allCreators.filter(
      (creator) =>
        creator.name.toLowerCase().includes(lowerCaseSearch) ||
        creator.interests?.some((interest) =>
          interest.toLowerCase().includes(lowerCaseSearch),
        ),
    );
  }, [creators, debouncedSearchQuery]);

  if (isLoading) {
    return <RevSpinner size="large" centered />;
  }

  if (creators.length === 0) {
    return (
      <RevEmptyState
        title="No Creators Available"
        text="No creators found for the selected interest group."
      />
    );
  }

  return (
    <div>
      <p className="typography-body-sm mb-2">Select Creator</p>
      <RevInput
        name="creatorSearch"
        type="text"
        placeholder="Search creators"
        size="md"
        label=""
        iconLeft={faMagnifyingGlass}
        enableClearButton={true}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClear={() => setSearchQuery("")}
        className="mb-3 shadow-none"
      />
      <div className="px-2 border-monochrome-200 bg-monochrome-50 border rounded-lg max-h-[60dvh] lg:h-[150px] overflow-y-auto scroll-vertical">
        {filteredCreators.map((creator) => {
          const isSelected = selectedCreatorId === creator.id;
          return (
            <button
              type="button"
              key={creator.id}
              onClick={() => onSelectCreator(creator.id)}
              className={`w-full my-2 bg-white rounded-lg p-2 grid grid-cols-[40px_1fr] gap-x-2 ${isSelected ? "outline-2 outline-primary-500" : "outline-1 outline-monochrome-200 hover:outline-primary-500"}`}
            >
              <RevAvatar
                variant="circle"
                size="medium"
                theme="light"
                image={
                  creator.creator_image_file ||
                  creator.creator_image_link ||
                  undefined
                }
                firstName={creator.name.split(" ")[0]}
                lastName={creator.name.split(" ").pop()}
              />
              <div className="flex flex-col text-left items-start">
                <p className="typography-body-bold-sm text-monochrome-900">
                  {creator.name}
                </p>
                <p className="typography-body-xs text-monochrome-500 w-full whitespace-normal break-words overflow-hidden line-clamp-2">
                  Total Followers:{" "}
                  {fullFormatNumbers({
                    value: creator.total_followers || 0,
                    noFormatNumber: true,
                    shortenValue: 1e3,
                  })}
                  {creator.interests?.length > 0 &&
                    ` | ${creator.interests.join(", ")}`}
                </p>
              </div>
            </button>
          );
        })}
      </div>
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
    </div>
  );
};
